import type { ICRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';

import type { IPipelineBaseStep, PaymentPipelineResult, PipelineStepContext } from '..';
import {
  CheckServiceModeStep,
  CommitOrderStep,
  EncryptionStep,
  Logger,
  PaymentPipeline,
  PaymentProcessor,
  ValidateCartStep,
  ValidateGiftCardStep,
  WorldpayApplePayConversionStep,
  WorldpayGooglePayConversionStep,
} from '..';
// Import GooglePayStep and ApplePayStep directly to allow platform-specific resolution (.ts vs .web.ts)
import { ApplePayStep } from '../steps/apple-pay-step';
import { GooglePayStep } from '../steps/google-pay-step';

type StepName =
  | 'checkServiceMode'
  | 'validateCart'
  | 'commitOrder'
  | 'encryption'
  | 'applePay'
  | 'googlePay'
  | 'worldpayApplePayConversion'
  | 'worldpayGooglePayConversion'
  | 'validateGiftCard';

type StepFactory = () => IPipelineBaseStep;
type StepFactoryMap = Record<StepName, StepFactory>;

export class PaymentOrchestrator {
  private steps: StepFactoryMap;
  private logger = new Logger('PaymentOrchestrator');
  private crmLogger?: ICRMEventsContext['logRBIEvent'];

  constructor(stepOverrides?: Partial<StepFactoryMap>) {
    this.steps = {
      checkServiceMode: () => new CheckServiceModeStep(),
      validateCart: () => new ValidateCartStep(),
      commitOrder: () => new CommitOrderStep(),
      encryption: () => new EncryptionStep(),
      applePay: () => new ApplePayStep(),
      googlePay: () => new GooglePayStep(),
      worldpayApplePayConversion: () => new WorldpayApplePayConversionStep(),
      worldpayGooglePayConversion: () => new WorldpayGooglePayConversionStep(),
      validateGiftCard: () => new ValidateGiftCardStep(),
      // Override with any overrides in testing
      ...stepOverrides,
    };
  }

  setCRMLogger(logger: ICRMEventsContext['logRBIEvent']): void {
    this.crmLogger = logger;
    this.logger.setCRMLogger(logger);
  }

  /**
   * Execute payment flow with appropriate pipeline
   *
   * @param context - Payment context with order and payment details
   * @param options - Execution options
   * @param options.skipToStep - Skip all steps before this one (for retries)
   */
  async execute(
    context: PipelineStepContext,
    options?: { skipToStep?: string }
  ): Promise<PaymentPipelineResult> {
    this.logger.log('Determining pipeline steps for:', {
      paymentMethod: context.paymentMethod,
      processor: context.processor,
      vaultCard: context.paymentMethod === 'newCard' ? context.cardData.vaultCard : undefined,
      skipToStep: options?.skipToStep,
    });

    const allSteps = this.assembleStepsArray(context);

    // If resuming from a specific step, skip earlier ones
    let steps = allSteps;
    if (options?.skipToStep) {
      const skipIndex = allSteps.findIndex(s => s.name === options.skipToStep);
      if (skipIndex > 0) {
        this.logger.log(`Resuming from step: ${options.skipToStep} (skipping ${skipIndex} steps)`);
        steps = allSteps.slice(skipIndex);
      }
    }
    // give logger for each step
    if (this.crmLogger) {
      steps.forEach(step => {
        if (step.setCRMLogger) {
          step.setCRMLogger(this.crmLogger!);
        }
      });
    }

    this.logger.log(
      `Pipeline will execute ${steps.length} steps:`,
      steps.map(s => s.name)
    );

    this.logger.logCRMEvent('PipelineStarted', {
      paymentMethod: context.paymentMethod,
      totalSteps: steps.length,
      isRetry: !!options?.skipToStep,
      stepNames: steps.map(s => s.name),
    });

    const pipeline = new PaymentPipeline(steps, context);

    if (this.crmLogger) {
      pipeline.setCRMLogger(this.crmLogger);
    }

    const result = await pipeline.execute();

    return result;
  }

  // Determine which steps to run based on payment context
  private assembleStepsArray(context: PipelineStepContext): IPipelineBaseStep[] {
    const steps: IPipelineBaseStep[] = [];

    switch (context.paymentMethod) {
      case 'savedCard':
        // Saved cards skip validation, encryption, and vaulting
        this.addCommonSteps(steps);
        return steps;

      case 'newCard': {
        // Step 1: Encrypt card data (processor-specific)
        steps.push(this.steps.encryption());
        // No separate vault step needed - CommitOrderStep handles it non-blocking

        // Steps 2-5: Common validation and commit
        this.addCommonSteps(steps);
        return steps;
      }

      case 'applePay':
        // Step 1: Request payment from Apple Pay (opens sheet, gets token)
        steps.push(this.steps.applePay());

        // Step 2: Worldpay requires token conversion, Orbital uses token directly
        if (context.processor === PaymentProcessor.WORLDPAY) {
          steps.push(this.steps.worldpayApplePayConversion());
        }

        this.addCommonSteps(steps);

        return steps;

      case 'googlePay':
        // Step 1: Request payment from Google Pay (opens sheet, gets token)
        steps.push(this.steps.googlePay());

        // Step 2: Worldpay requires token conversion, Orbital uses token directly
        if (context.processor === PaymentProcessor.WORLDPAY) {
          steps.push(this.steps.worldpayGooglePayConversion());
        }

        this.addCommonSteps(steps);

        return steps;

      case 'giftCard':
        steps.push(this.steps.validateGiftCard());
        this.addCommonSteps(steps);

        return steps;

      // Empty pipeline; TypeScript knows this is unreachable (exhaustive check passed)
      default:
        this.logger.logError(
          `Unknown payment method: ${(context as PipelineStepContext).paymentMethod}`
        );
        return [];
    }
  }

  // These steps are executed regardless of the payment method
  private addCommonSteps(steps: IPipelineBaseStep[]): void {
    steps.unshift(this.steps.checkServiceMode(), this.steps.validateCart());
    steps.push(this.steps.commitOrder());
  }
}
